// Creating and editing in a dialog instead of on their own page.
//
// One dialog serves the whole app, and one dialog serves both actions: the
// fields come from the resource's existing /new or /:id/edit route, fetched as
// a fragment and dropped into the box. So there is no modal markup per
// resource, no second copy of any form, and create and edit differ only in the
// URL that was fetched - the server already renders both from one _form.pug.
//
// The routes stay real pages. A link opened in a new tab, a bookmark, a reload
// after a validation error: all of them still render the full page, because the
// fragment and the page are the same route and the same template.

import { notifyError, raiseOverlays } from './notify.js';
import { setzeModalTitel, stelleTitelWiederHer } from './modalTitel.js';

// Marks the request as wanting only the form body. Same protocol as the list
// fragments - see utils/renderFragment.js.
const FRAGMENT_HEADER = 'X-Fragment';

// What the modal calls after a successful save. Set per opening, since it is
// the caller - a list, a detail page - that knows how to show the result.
let onSaved = null;

function elements() {
  return {
    modal: document.getElementById('form-modal'),
    body: document.getElementById('form-modal-body')
  };
}

function showSpinner(body) {
  body.innerHTML =
    '<div class="flex justify-center py-12">' +
    '<span class="loading loading-spinner loading-lg text-primary"></span>' +
    '</div>';
}

// Fetches a form and shows it. `url` is the ordinary route - '/kunden/new' or
// '/kunden/123/edit' - so nothing new has to be routed for the modal's sake.
async function openForm(url) {
  const { modal, body } = elements();
  if (!modal || !body) {
    // No dialog on this page, so let the browser do what it would have done
    // without us.
    window.location.href = url;
    return;
  }

  showSpinner(body);
  bindeTitelWiederherstellung(modal);
  modal.showModal();

  // showModal() promotes the dialog into the top layer, above anything
  // promoted before it - a Loading overlay left up by whatever opened this
  // form included. Promoting the overlay host again puts it back on top.
  raiseOverlays();

  try {
    const response = await window.fetch(url, {
      headers: { [FRAGMENT_HEADER]: '1' },
      credentials: 'same-origin'
    });

    // A dead session redirects to /login. Follow it rather than pasting a login
    // form into the dialog.
    if (
      response.redirected &&
      new URL(response.url).pathname !== new URL(url, window.location.origin).pathname
    ) {
      window.location.href = response.url;
      return;
    }

    if (!response.ok) throw new Error(`Form request failed: ${response.status}`);

    body.innerHTML = await response.text();

    // The per-resource module binds submit by id on the form it owns, and that
    // form only just arrived, so it has to be told to look again.
    document.dispatchEvent(new window.CustomEvent('form-modal:opened', { detail: { url } }));

    // The fragment is in the DOM now, so its heading and fields can be read.
    setzeModalTitel(body);

    // The first field, so the dialog is usable from the keyboard immediately.
    // Skips hidden inputs, which is where the id lives.
    body.querySelector('input:not([type="hidden"]), select, textarea')?.focus();
  } catch (err) {
    modal.close();
    notifyError('Das Formular konnte nicht geladen werden. Bitte versuchen Sie es erneut.');
    throw err;
  }
}

// Escape and a backdrop click close the dialog without going through
// closeFormModal, so the title is restored from the dialog's own close event
// rather than from there - that one fires whatever closed it.
let titelGebunden = false;

function bindeTitelWiederherstellung(modal) {
  if (titelGebunden || !modal) return;
  modal.addEventListener('close', stelleTitelWiederHer);
  titelGebunden = true;
}

export function closeFormModal() {
  elements().modal?.close();
}

// Called by a form module once its save succeeded: closes the dialog and hands
// control back to whatever opened it.
//
// The handler runs inside the caller's try/catch, and the save has already
// happened by the time we get here - so anything it throws would be reported as
// a failed save when nothing failed. A detail page's onSaved reloads the page,
// and work started during the unload throws readily. Hence the catch: the
// record is saved either way, and the user is told so.
export function formModalSaved() {
  closeFormModal();
  const handler = onSaved;
  onSaved = null;
  if (!handler) return;

  try {
    handler();
  } catch (err) {
    // Not notifyError: the save worked, and the caller is about to say so.
    console.error('onSaved handler failed after a successful save', err);
  }
}

// True while a form is being shown in the dialog, which is how a form module
// decides whether to close the dialog and refresh a list or to navigate the way
// it always did.
export function isFormModalOpen() {
  return Boolean(elements().modal?.open);
}

/**
 * Turns links that would navigate to a form into ones that open the dialog.
 *
 * Delegated from a container, so the links inside a list fragment that has just
 * been swapped in are live without rebinding anything.
 *
 * @param {object} options
 * @param {HTMLElement|Document} [options.container] where to listen; defaults to document
 * @param {string} options.selector  which links to intercept, e.g. 'a[href$="/edit"]'
 * @param {function} [options.onSaved] run after a successful save
 */
export function initFormModalLinks({ container = document, selector, onSaved: saved } = {}) {
  const { modal } = elements();
  if (!modal || !selector) return;

  container.addEventListener('click', (event) => {
    const link = event.target.closest(selector);
    if (!link || !container.contains(link)) return;

    // Ctrl/Cmd/middle click means "open in a new tab", and the route is a real
    // page, so let the browser have it.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    event.preventDefault();
    onSaved = saved || null;
    openForm(link.getAttribute('href'));
  });
}

/**
 * Opens a form in the dialog directly, for a button that is not a link.
 *
 * @param {string} url  the form route to fetch
 * @param {function} [saved]  run after a successful save
 */
export function openFormModal(url, saved) {
  onSaved = saved || null;
  return openForm(url);
}
